/**
 * Advanced Component Resolution Utilities
 * Type-aware schema matching with compatibility scoring
 */

import React from 'react';
import {
	CAPABILITY_INDEX,
	RENDER_INDEX,
	getAllComponents,
	type ComponentEntry,
} from './componentRegistry';
import type {
	UIComponentContract,
	PropertyType,
	PropertyShape,
	SchemaMatchResult,
	XUIMetadata,
	OpenAPISchema,
} from '../types/componentContract';

/**
 * Advanced schema matching with type compatibility checking
 */
export function advancedSchemaMatch(
	contractShape: PropertyShape,
	openAPISchema: OpenAPISchema | null | undefined,
	required?: string[]
): SchemaMatchResult {
	// Handle null/undefined schema
	if (!openAPISchema) {
		return {
			compatible: false,
			score: 0,
			missingRequired: required || Object.keys(contractShape),
			typeMismatches: [],
			extraProperties: [],
		};
	}

	// Handle empty contract shapes (generic components that accept any structure)
	const contractKeys = Object.keys(contractShape);
	if (contractKeys.length === 0) {
		// Empty shape means component accepts any structure
		return {
			compatible: true,
			score: 0.8, // Good score for generic match
			missingRequired: [],
			typeMismatches: [],
			extraProperties: [],
		};
	}

	// Handle array schemas - if schema is array, check if contract expects array
	if (openAPISchema.type === 'array') {
		const hasArrayProperty = contractKeys.some((key) => {
			const contractType = contractShape[key];
			return (
				contractType === 'array' ||
				(typeof contractType === 'string' &&
					contractType.includes('array'))
			);
		});

		if (hasArrayProperty) {
			// Contract accepts arrays - this is compatible
			return {
				compatible: true,
				score: 0.9, // High score for array match
				missingRequired: [],
				typeMismatches: [],
				extraProperties: [],
			};
		}

		// Contract doesn't expect array - not compatible
		return {
			compatible: false,
			score: 0.3,
			missingRequired: required || contractKeys,
			typeMismatches: [],
			extraProperties: [],
		};
	}

	const schemaProps = openAPISchema.properties || {};
	const requiredKeys = required || contractKeys;

	const missingRequired: string[] = [];
	const typeMismatches: Array<{
		property: string;
		expected: PropertyType;
		actual: string;
	}> = [];
	const extraProperties: string[] = [];

	// Check required properties
	requiredKeys.forEach((key) => {
		if (!schemaProps[key]) {
			missingRequired.push(key);
		} else {
			// Type compatibility check
			const contractType = contractShape[key];
			const schemaProp = schemaProps[key];
			const schemaType =
				schemaProp.type || inferTypeFromSchema(schemaProp);

			if (!isTypeCompatible(contractType, schemaType, schemaProp)) {
				typeMismatches.push({
					property: key,
					expected: contractType as PropertyType,
					actual: schemaType,
				});
			}
		}
	});

	// Check for extra properties (not in contract but in schema)
	Object.keys(schemaProps).forEach((key) => {
		if (!contractKeys.includes(key)) {
			extraProperties.push(key);
		}
	});

	// Calculate compatibility score (0-1)
	const totalChecks = requiredKeys.length;
	const passedChecks =
		totalChecks - missingRequired.length - typeMismatches.length;
	const score = totalChecks > 0 ? passedChecks / totalChecks : 1;

	// Consider extra properties as bonus (up to 0.1 score increase)
	const extraBonus = Math.min(extraProperties.length * 0.01, 0.1);

	return {
		compatible: missingRequired.length === 0 && typeMismatches.length === 0,
		score: Math.min(score + extraBonus, 1),
		missingRequired,
		typeMismatches,
		extraProperties,
	};
}

/**
 * Type compatibility checking
 */
function isTypeCompatible(
	contractType: PropertyType | PropertyShape | PropertyShape[],
	schemaType: string,
	schemaProp: any
): boolean {
	// Handle optional types
	if (typeof contractType === 'string' && contractType.endsWith('?')) {
		return isTypeCompatible(
			contractType.slice(0, -1) as PropertyType,
			schemaType,
			schemaProp
		);
	}

	// Direct type mapping
	const typeMap: Record<string, string[]> = {
		string: ['string'],
		number: ['number', 'integer'],
		boolean: ['boolean'],
		date: ['string'], // Dates are strings in JSON
		email: ['string'],
		url: ['string'],
		image: ['string'],
		array: ['array'],
		object: ['object'],
	};

	if (typeof contractType === 'string') {
		const compatibleTypes = typeMap[contractType] || [contractType];
		return compatibleTypes.includes(schemaType);
	}

	// Nested shapes - recursive check
	if (typeof contractType === 'object' && schemaType === 'object') {
		// For nested objects, we do a simplified check
		// Full nested matching would require recursive schema matching
		return true; // Assume compatible if both are objects
	}

	return false;
}

/**
 * Infer type from OpenAPI schema property
 */
function inferTypeFromSchema(schemaProp: any): string {
	if (schemaProp.type) return schemaProp.type;
	if (schemaProp.format === 'date' || schemaProp.format === 'date-time')
		return 'date';
	if (schemaProp.format === 'email') return 'email';
	if (schemaProp.format === 'uri' || schemaProp.format === 'url')
		return 'url';
	if (schemaProp.format === 'binary') return 'image';
	if (schemaProp.enum) return 'string';
	return 'string'; // Default
}

/**
 * Main component resolution function with advanced matching
 */
export function resolveComponent(
	xui: XUIMetadata,
	responseSchema: OpenAPISchema | null | undefined
): React.ComponentType<any> | null {
	// If no schema provided, try to match by capability and render only
	if (!responseSchema) {
		const capabilityMatches = CAPABILITY_INDEX.get(xui.capability) || [];
		const renderMatches = RENDER_INDEX.get(xui.renders) || [];

		const candidates = capabilityMatches.filter((c) =>
			renderMatches.some((r) => r.component === c.component)
		);

		// Return highest priority match if available
		return (
			candidates.sort(
				(a, b) =>
					(b.contract.priority || 0) - (a.contract.priority || 0)
			)[0]?.component || null
		);
	}
	// Fast path: use pre-computed indexes
	const capabilityMatches = CAPABILITY_INDEX.get(xui.capability) || [];
	const renderMatches = RENDER_INDEX.get(xui.renders) || [];

	// Intersection of capability and render matches
	const candidates = capabilityMatches.filter((c) =>
		renderMatches.some((r) => r.component === c.component)
	);

	if (candidates.length === 0) {
		// Fallback: check all components (slower but comprehensive)
		const allComponents = getAllComponents();
		const fallbackCandidates = allComponents
			.filter(
				({ contract }) =>
					contract.accepts.capability === xui.capability &&
					contract.renders === xui.renders
			)
			.map((candidate) => {
				const match = advancedSchemaMatch(
					candidate.contract.accepts.shape,
					responseSchema,
					candidate.contract.accepts.required
				);
				return { candidate, match };
			})
			.filter(({ match }) => match.compatible)
			.sort((a, b) => {
				// Sort by score, then priority
				if (b.match.score !== a.match.score) {
					return b.match.score - a.match.score;
				}
				return (
					(b.candidate.contract.priority || 0) -
					(a.candidate.contract.priority || 0)
				);
			});

		return fallbackCandidates[0]?.candidate.component || null;
	}

	// Advanced matching on candidates
	const scoredCandidates = candidates.map((candidate) => {
		const match = advancedSchemaMatch(
			candidate.contract.accepts.shape,
			responseSchema,
			candidate.contract.accepts.required
		);
		return { candidate, match };
	});

	// Filter compatible and sort by score + priority
	return (
		scoredCandidates
			.filter(({ match }) => match.compatible || match.score > 0.7) // Allow 70%+ matches
			.sort((a, b) => {
				if (b.match.score !== a.match.score) {
					return b.match.score - a.match.score;
				}
				return (
					(b.candidate.contract.priority || 0) -
					(a.candidate.contract.priority || 0)
				);
			})[0]?.candidate.component || null
	);
}

/**
 * Get all compatible components (for fallback UI or selection)
 */
export function getCompatibleComponents(
	xui: XUIMetadata,
	responseSchema: OpenAPISchema | null | undefined,
	minScore: number = 0.5
): Array<{ component: React.ComponentType<any>; match: SchemaMatchResult }> {
	const allComponents = getAllComponents();

	// If no schema, match by capability and render only
	if (!responseSchema) {
		return allComponents
			.filter(
				({ contract }) =>
					contract.accepts.capability === xui.capability &&
					contract.renders === xui.renders
			)
			.map((candidate) => ({
				component: candidate.component,
				match: {
					compatible: true,
					score: 0.5, // Default score when no schema
					missingRequired: [],
					typeMismatches: [],
					extraProperties: [],
				} as SchemaMatchResult,
			}))
			.sort(
				(a, b) =>
					(b.component as any).uiContract?.priority ||
					0 - (a.component as any).uiContract?.priority ||
					0
			);
	}

	return allComponents
		.filter(
			({ contract }) =>
				contract.accepts.capability === xui.capability &&
				contract.renders === xui.renders
		)
		.map((candidate) => {
			const match = advancedSchemaMatch(
				candidate.contract.accepts.shape,
				responseSchema,
				candidate.contract.accepts.required
			);
			return { component: candidate.component, match };
		})
		.filter(({ match }) => match.score >= minScore)
		.sort((a, b) => {
			if (b.match.score !== a.match.score) {
				return b.match.score - a.match.score;
			}
			return 0;
		});
}
